import { useMemo } from "react";
import type {
	SettingsItemResponseDTO,
	SettingsResponseDTO,
} from "web_myplann/Infrastructure/Api/Api";
import { useNotificationSettingsQuery } from "web_myplann/More/NotificationSettings/Api/useNotificationSettingsQuery";
import type { NotificationSettingKey } from "web_myplann/More/NotificationSettings/Types/NotificationSettingKey";

type ContractsSectionItem = {
	isToggleable: boolean;
	isEnabled: boolean;
};

type ContractsSectionDerivation = {
	/** Stays true forever while the user lacks the notification center right (query disabled). */
	isPending: boolean;
	isError: boolean;
	/** `data !== undefined`; a resolved `null` (404 → not eligible) counts as data. */
	hasData: boolean;
	isSectionVisible: boolean;
	isBannerVisible: boolean;
	items: Record<NotificationSettingKey, ContractsSectionItem>;
};

const CONTRACT_KEYS = [
	"contractEnd",
	"fixationEnd",
	"contractAnniversary",
] as const satisfies readonly NotificationSettingKey[];

const EMPTY_ITEM: ContractsSectionItem = {
	isToggleable: false,
	isEnabled: false,
};

const toItem = (
	entry: SettingsItemResponseDTO | undefined,
): ContractsSectionItem => ({
	isToggleable: Boolean(entry?.available),
	isEnabled: Boolean(entry?.enabled),
});

const emptyItems = (): Record<
	NotificationSettingKey,
	ContractsSectionItem
> => ({
	contractEnd: EMPTY_ITEM,
	fixationEnd: EMPTY_ITEM,
	contractAnniversary: EMPTY_ITEM,
	shortTermContracts: EMPTY_ITEM,
});

const buildItems = (
	data: SettingsResponseDTO,
	isSectionVisible: boolean,
): Record<NotificationSettingKey, ContractsSectionItem> => {
	const shortTerm = toItem(data.shortTermContracts);
	return {
		contractEnd: toItem(data.contractEnd),
		fixationEnd: toItem(data.fixationEnd),
		contractAnniversary: toItem(data.contractAnniversary),
		shortTermContracts: {
			isToggleable: shortTerm.isToggleable && isSectionVisible,
			isEnabled: shortTerm.isEnabled,
		},
	};
};

export const useContractsSectionDerivation = (): ContractsSectionDerivation => {
	const { data, isPending, isError } = useNotificationSettingsQuery();

	const hasData = data !== undefined;

	return useMemo(() => {
		if (!data) {
			return {
				isPending,
				isError,
				hasData,
				isSectionVisible: false,
				isBannerVisible: false,
				items: emptyItems(),
			};
		}

		const contractItems = CONTRACT_KEYS.map((key) => toItem(data[key]));
		const isSectionVisible = contractItems.some((item) => item.isToggleable);
		const isBannerVisible =
			isSectionVisible && contractItems.some((item) => !item.isToggleable);

		return {
			isPending,
			isError,
			hasData,
			isSectionVisible,
			isBannerVisible,
			items: buildItems(data, isSectionVisible),
		};
	}, [data, isPending, isError]);
};
